import { useCallback, useRef, ReactNode, useEffect, MouseEvent } from 'react';

import { TooltipWrapperStyled } from './tooltip.style';
import { positionAndDisplayTooltip, hideTooltip } from './tooltip.utils';
import { THUMBNAIL_HOVER_DURATION } from 'approot/shared/styles/thumbnails.style';

type Props = {
  hoverText?: string;
  hoverHeading?: string;
  hoverHeadingSecondary?: string;
  multiline?: boolean;
  children: ReactNode;
  backgroundColour?: string;
  className?: string;
  svgContent?: ReactNode;
  forceBelow?: boolean;
};

const TooltipWrapper = ({
  hoverText,
  hoverHeading,
  hoverHeadingSecondary,
  multiline,
  children,
  backgroundColour,
  className,
  svgContent,
  forceBelow,
}: Props) => {
  const tooltipWrapper = useRef<HTMLDivElement | null>(null);
  const mouseOverTimeout = useRef<number | undefined>(undefined);

  const handleMouseOver = useCallback(() => {
    if (tooltipWrapper.current && hoverText) {
      if (mouseOverTimeout.current) clearTimeout(mouseOverTimeout.current);
      // delay until card hover has finished
      mouseOverTimeout.current = window.setTimeout(
        () =>
          positionAndDisplayTooltip(hoverText, tooltipWrapper.current!, {
            heading: hoverHeading,
            multiline,
            secondaryHeading: hoverHeadingSecondary,
            backgroundColour,
            svgContent,
            forceBelow,
          }),
        THUMBNAIL_HOVER_DURATION
      );
    }
  }, [
    hoverText,
    hoverHeading,
    hoverHeadingSecondary,
    backgroundColour,
    multiline,
    mouseOverTimeout,
    svgContent,
    forceBelow,
  ]);

  const handleMouseLeave = useCallback(() => {
    if (mouseOverTimeout.current) clearTimeout(mouseOverTimeout.current);
    hideTooltip();
  }, [mouseOverTimeout]);

  const handleOnClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    if (mouseOverTimeout.current) clearTimeout(mouseOverTimeout.current);

    hideTooltip();
  }, []);

  useEffect(() => {
    return () => hideTooltip();
  }, []);

  return (
    <TooltipWrapperStyled
      ref={ref => {
        tooltipWrapper.current = ref;
      }}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseOver}
      onBlur={handleMouseLeave}
      onClick={handleOnClick}
      className={className}
    >
      {children}
    </TooltipWrapperStyled>
  );
};

export default TooltipWrapper;
